import { inject, injectable, injectWithTransform } from "tsyringe";
import {
  HttpClient,
  Logger,
  LoggerTransformer,
  Skin,
  WikiConfig,
} from "@identity/utils";
import type {
  DataWarehouseParams,
  TimeBasedParams,
  EventParams,
  TrackingParams,
} from "./types";
import { OptInService } from "../OptInService";
import { WINDOW } from "@identity/symbols";

export interface IdentityEventParams {
  partner_name: string;
  partner_identity_id: string;
}

@injectable()
export class IdentityEventSender {
  private dataWarehouseUrl =
    "https://beacon.wikia-services.com/__track/special/identityinfo";
  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "DataWarehouseService")
    private logger: Logger,
    @inject(HttpClient) private httpClient: HttpClient,
    @inject(Skin) private skin: Skin,
    @inject(OptInService) private optInService: OptInService,
    @inject(WINDOW)
    private readonly window: Window,
    @inject(WikiConfig)
    private readonly wikiConfig: WikiConfig,
  ) {}

  send(payload: IdentityEventParams) {
    this.sendEvent(payload, this.dataWarehouseUrl);
  }

  private sendEvent(options: EventParams, trackingURL: string): void {
    const params: DataWarehouseParams = {
      ...options,
      ...this.getTimeBasedParams(),
      ...this.getTrackingParams(),
    };
    const url = this.buildDataWarehouseUrl(params, trackingURL);

    if (url) {
      this.httpClient.get(url);
    }
  }

  private getTrackingParams(): TrackingParams {
    return {
      session_id: this.window.fandomContext?.tracking?.sessionId,
      pv_number: this.window.fandomContext?.tracking?.pvNumber,
      pv_number_global: this.window.fandomContext?.tracking?.pvNumberGlobal,
      pv_unique_id: this.window.fandomContext?.tracking?.pvUID,
      beacon: this.window.fandomContext?.tracking?.beaconId,
      ua: this.window.navigator?.userAgent,
      c: this.wikiConfig.get("wgCityId") || "unknown",
      ck: this.wikiConfig.get("dsSiteKey") || "unknown",
      lc: this.wikiConfig.get("wgUserLanguage") || "unknown",
      s: this.skin.get(),
      u: this.optInService.trackingAllowed
        ? this.wikiConfig.get("wgUserId") || 0
        : -1,
      a: this.window.fandomContext?.page?.articleId || -1,
      x: this.wikiConfig.get("wgDBname") || "unknown",
      n: this.wikiConfig.get("wgNamespaceNumber") || -1,
    };
  }

  private getTimeBasedParams(): TimeBasedParams {
    return {
      cb: Math.floor(Math.random() * 99999),
      url: document.URL,
    };
  }

  private buildDataWarehouseUrl(
    params: DataWarehouseParams,
    baseUrl: string,
  ): string {
    if (!baseUrl) {
      this.logger.error("Error building DWH tracking URL");
      return;
    }

    const queryParams = new URLSearchParams(
      params as unknown as Dictionary<string>,
    );
    queryParams.sort();
    return `${baseUrl}?${queryParams.toString()}`;
  }
}
